import { Metamodel } from 'app/model/base/model';
import { NotebookActivity } from 'app/model/pipeline/notebook.activity';
import { SparkJarActivity } from 'app/model/pipeline/spark-jar.activity';
import { SparkPythonActivity } from 'app/model/pipeline/spark-python.activity';
import { CustomActivity } from 'app/model/pipeline/custom.activity';
import { USqlActivity } from 'app/model/pipeline/usql.activity';
import { AzureFunctionActivity } from 'app/model/pipeline/azure-function.activity';
import { WebActivity } from 'app/model/pipeline/web.activity';
import { FailActivity } from 'app/model/pipeline/fail.activity';
import { SparkActivity } from 'app/model/pipeline/spark.activity';
import { HiveActivity } from 'app/model/pipeline/hive.activity';
import { MapReduceActivity } from 'app/model/pipeline/map-reduce.activity';
import { PigActivity } from 'app/model/pipeline/pig.activity';
import { StreamingActivity } from 'app/model/pipeline/streaming.activity';
import { stringIsNullOrEmpty } from 'shell-models-lib';
import errorList from 'assets/json/errorcode-doc-mapping.json';
import { showActivityDocLinkEntry } from './activity-type.util';
import { baseUrlToFabricUrlMap } from './pipeline-copilot/error-explain.util';

class ActivityRunTroubleshootingInfo {
    public static docLink: string = 'https://go.microsoft.com/fwlink/?linkid=2097182';
    public static docLinkFabric: string = 'https://learn.microsoft.com/fabric/data-factory/pipeline-troubleshoot-guide';
    public static admsDocLink: string = 'https://go.microsoft.com/fwlink/?linkid=2112593';
    public static admsDocLinkFabric: string = 'https://learn.microsoft.com/fabric/data-factory/connector-troubleshoot-overview';
    public static ssisDocLink: string = __IS_FABRIC_DPE__ ? 'https://go.microsoft.com/fwlink/?linkid=2359108' : 'https://go.microsoft.com/fwlink/?linkid=2115004';
    public static dataflowDocLink: string = 'https://go.microsoft.com/fwlink/?linkid=2117168';
    public static dataflowDocLinkFabric: string = 'https://learn.microsoft.com/fabric/data-factory/dataflow-activity#troubleshooting-tips';
    public static failActivityDocLink: string = 'https://docs.microsoft.com/azure/data-factory/control-flow-fail-activity';
    public static activitiesWithDocAvailable: Set<string> = new Set<string>([
        Metamodel.modelFor(NotebookActivity).name,
        Metamodel.modelFor(SparkJarActivity).name,
        Metamodel.modelFor(SparkPythonActivity).name,
        Metamodel.modelFor(USqlActivity).name,
        Metamodel.modelFor(AzureFunctionActivity).name,
        Metamodel.modelFor(CustomActivity).name,
        Metamodel.modelFor(SparkActivity).name,
        Metamodel.modelFor(HiveActivity).name,
        Metamodel.modelFor(MapReduceActivity).name,
        Metamodel.modelFor(PigActivity).name,
        Metamodel.modelFor(StreamingActivity).name,
        Metamodel.modelFor(WebActivity).name,
        Metamodel.modelFor(FailActivity).name
    ]);
}

interface IAdmsTroubleShootingObj {
    base_url: string;
    Category: string;
    alternative_questions?: string[]; // deprecate
    path?: string; // url page anchor
}

const amdsAlternativeLinks: Map<string, IAdmsTroubleShootingObj> = new Map();

function initAdmsTroubleShootingMapping(): void {
    // temporary fix, as some backend API response uses digits (ie: 22300) errorCode (should deprecate) while others uses strings (ie: SqlFailedToConnect)
    if (amdsAlternativeLinks.size === 0) { // init amdsAlternativeLinks with alternative_question links
        const typedErrorList = errorList as Record<string, IAdmsTroubleShootingObj>;
        for (const key in typedErrorList) {
            amdsAlternativeLinks.set(key, typedErrorList[key]);
            if (!typedErrorList[key].path) {
                // only iterate through 'alternative_questions', if 'path' doesn't exist.
                for (const altKey of typedErrorList[key].alternative_questions) {
                    amdsAlternativeLinks.set(altKey, {
                        ...typedErrorList[key],
                        path: key
                    });
                }
            }
        }
    }
}
// got transform activity doc link
export function getActivityRunTroubleshootingDoc(errorCode?: string, activityType?: string): string {
    const baseUrl = __IS_FABRIC_DPE__ ? ActivityRunTroubleshootingInfo.docLinkFabric : ActivityRunTroubleshootingInfo.docLink;
    
    // for web activity error code 2105, the anchor hashtag is 2105-1, since there's another 2105 on the page
    if (activityType === 'WebActivity' && errorCode === '2105') {
        return `${baseUrl}#error-code-${errorCode}-1`;
    }
    return errorCode ? `${baseUrl}#error-code-${errorCode}` : baseUrl;
}

export function hasAdmsActivityRunTroubleshootingDoc(errorCode: string): boolean {
    initAdmsTroubleShootingMapping();
    return amdsAlternativeLinks.has(errorCode);
}

export function getAdmsActivityRunTroubleshootingItem(errorCode: string): IAdmsTroubleShootingObj {
    initAdmsTroubleShootingMapping();
    return amdsAlternativeLinks.get(errorCode);
}

// get adms doc link
export function getAdmsActivityRunTroubleshootingDoc(errorCode?: string): string {
    initAdmsTroubleShootingMapping();

    if (errorCode && amdsAlternativeLinks.get(errorCode)?.base_url) {
        const troubleshootingObj = amdsAlternativeLinks.get(errorCode);
        let baseUrl = troubleshootingObj.base_url;
        
        // Check if we should remap the URL for Fabric
        if (__IS_FABRIC_DPE__ && baseUrlToFabricUrlMap[baseUrl]) {
            baseUrl = baseUrlToFabricUrlMap[baseUrl];
        }
        
        return `${baseUrl}#error-code-${(troubleshootingObj.path || errorCode).toLowerCase()}`;
    }
    
    return !stringIsNullOrEmpty(errorCode) 
        ? `${__IS_FABRIC_DPE__ ? ActivityRunTroubleshootingInfo.admsDocLinkFabric : ActivityRunTroubleshootingInfo.admsDocLink}#error-code-${errorCode.toLowerCase()}` 
        : (__IS_FABRIC_DPE__ ? ActivityRunTroubleshootingInfo.admsDocLinkFabric : ActivityRunTroubleshootingInfo.admsDocLink);
}

export function showActivityRunTroubleshootingLink(activityType: string): boolean {
    return ActivityRunTroubleshootingInfo.activitiesWithDocAvailable.has(activityType);
}

export function getSsisActivityRunTroubleshootingDoc(): string {
    return ActivityRunTroubleshootingInfo.ssisDocLink;
}

// got dataflow activity doc link
export function getDataflowActivityRunTroubleshootingDoc(errorCode?: string): string {
    if (__IS_FABRIC_DPE__) {
        return ActivityRunTroubleshootingInfo.dataflowDocLinkFabric;
    }
    return errorCode ? `${ActivityRunTroubleshootingInfo.dataflowDocLink}#error-code-${errorCode.toLowerCase()}` : ActivityRunTroubleshootingInfo.dataflowDocLink;
}

export function getFailActivityRunTroubleshootingDoc(): string {
    return ActivityRunTroubleshootingInfo.failActivityDocLink;
}

export function getDocLinkType(activityType: string): string {
    switch (activityType) {
        case 'ExecuteSSISPackage':
            return 'SSISTroubleshootingDocLink-Click';
        case 'ExecuteDataFlow':
        case 'RefreshDataflow':
            return 'DataFlowsTroubleshootingDocLink-Click';
        default:
            if (showActivityDocLinkEntry(activityType)) {
                return 'ADMSTroubleshootingDocLink-Click';
            }
            return 'ActivityTroubleshootingDocLink-Click';
    }
}

export function calcDocLink(activityType: string, errorCode: string, admsErrorCodeForDocLink: string): string {
    switch (activityType) {
        case 'ExecuteSSISPackage':
            return getSsisActivityRunTroubleshootingDoc();
        case 'ExecuteDataFlow':
        case 'RefreshDataflow':
            return getDataflowActivityRunTroubleshootingDoc(errorCode);
        case 'Fail':
            return getFailActivityRunTroubleshootingDoc();
        default:
            if (showActivityDocLinkEntry(activityType)) {
                return getAdmsActivityRunTroubleshootingDoc(admsErrorCodeForDocLink);
            }
            if (showActivityRunTroubleshootingLink(activityType)) {
                return getActivityRunTroubleshootingDoc(errorCode, activityType);
            }
            return undefined;
    }
}
